import type { SettlementStatus } from '@progear/finance-core';
import { settlementStatusStyle } from '@/lib/cashFlow';

/**
 * A compact donut/PIE of the paid share (`paidPct`, a finance-core percent — NOT money,
 * so the dasharray geometry here is allowed under L9). The filled arc is clamped to a full
 * ring at ≥100 % (an overpayment still reads "full"), while the centre label shows the true
 * percentage so a 120 % overpayment is still legible.
 */
export function SettlementRing({
  pct,
  status,
  kind,
  size = 52,
}: {
  pct: number;
  status: SettlementStatus;
  kind: 'income' | 'cost';
  size?: number;
}) {
  const radius = (size - 8) / 2;
  const circumference = 2 * Math.PI * radius;
  const filled = pct >= 100 ? circumference : (circumference * pct) / 100;
  // Single source: the same (status, kind) → colour map the badge uses (L14 one truth).
  const stroke = settlementStatusStyle(status, kind).ring;
  const center = size / 2;

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      className="shrink-0"
      role="img"
      aria-label={`${pct}% tasutud`}
    >
      <circle cx={center} cy={center} r={radius} fill="none" stroke="hsl(var(--border))" strokeWidth={4} />
      <circle
        cx={center}
        cy={center}
        r={radius}
        fill="none"
        stroke={stroke}
        strokeWidth={4}
        strokeLinecap="round"
        strokeDasharray={`${filled} ${circumference - filled}`}
        transform={`rotate(-90 ${center} ${center})`}
      />
      <text
        x="50%"
        y="50%"
        dominantBaseline="central"
        textAnchor="middle"
        className="text-[11px] font-semibold tabular-nums"
        style={{ fill: stroke }}
      >
        {pct}%
      </text>
    </svg>
  );
}
